import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "../auth";
import { useDebounce } from "../shared/useDebounce";
import { ilikeOrValue } from "@/lib/postgrestOr";

export interface LeadSearchResult {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company_id: string | null;
  companies: {
    id: string;
    name: string;
    website: string | null;
    industry: string | null;
  } | null;
}

export function useLeadSearch() {
  const { orgId } = useCurrentOrgId();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["lead-search", orgId, debouncedQuery],
    queryFn: async () => {
      if (!orgId) return [];

      let q = supabase
        .from("leads")
        .select("id, name, email, phone, company_id, companies(id, name, website, industry)")
        .eq("org_id", orgId);

      if (debouncedQuery.length >= 2) {
        q = q.or(
          `name.ilike.${ilikeOrValue(debouncedQuery)},email.ilike.${ilikeOrValue(debouncedQuery)}`,
        );
        q = q.limit(10);
      } else {
        q = q.order("updated_at", { ascending: false }).limit(5);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data as LeadSearchResult[];
    },
    enabled: !!orgId,
    staleTime: 10_000,
  });

  return { leads, isLoading, query, setQuery };
}

/** Fetch a single lead by ID (for pre-populating default selection) */
export function useLeadById(leadId: string | undefined) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ["lead-by-id", orgId, leadId],
    queryFn: async () => {
      if (!orgId || !leadId) return null;

      const { data, error } = await supabase
        .from("leads")
        .select("id, name, email, phone, company_id, companies(id, name, website, industry)")
        .eq("id", leadId)
        .eq("org_id", orgId)
        .single();

      if (error) throw error;
      return data as LeadSearchResult;
    },
    enabled: !!orgId && !!leadId,
    staleTime: 60_000,
  });
}
